import { Injectable } from '@angular/core';
import {
  BudgetLoaListParams,
  LoaListParams,
  LoaParams,
} from '@shared/models/loa.model';
import { ApiService } from 'src/app/core/http/api.service';
import { API_URL } from 'src/app/shared/service/api.constant';
import {
  LoaDetail,
  LoaListItem,
  LoaUserTypeEnum,
  Member,
  NonUser,
} from './loa.model';
import { TranslateService } from '@ngx-translate/core';
import {
  checkEmptyApprover,
  checkMail,
  checkPhoneNumber,
} from './utils';
import { Marker } from '../../memos/components/upload-memos/pdf-signature-customizer/pdf-signature-customizer.model';

@Injectable({
  providedIn: 'root',
})
export class LoaService {
  constructor(
    private http: ApiService,
    private translate: TranslateService,
  ) {}

  getLOAList(params?: LoaListParams) {
    return this.http.get<LoaListItem[]>(API_URL.loa_groups, {
      exclude_memo_type: 'budget_request',
      ...params,
    });
  }

  getBudgetLoaList(params?: BudgetLoaListParams) {
    return this.http.get<LoaListItem[]>(API_URL.budget_loa_groups, {
      ...params,
    });
  }

  getBudgetLOAList(params?: LoaListParams) {
    return this.http.get<LoaListItem[]>(API_URL.budget_loa_groups, {
      ...params,
    });
  }

  createNewLOA(data: any) {
    return this.http.post(API_URL.loa_groups, data);
  }

  createNewBudgetLOA(data: any) {
    return this.http.post(API_URL.budget_loa_groups, data);
  }

  updateLOA(id: number, data: any) {
    return this.http.put(API_URL.loa_groups + id + '/', data);
  }

  updateBudgetLOA(id: number, data: any) {
    return this.http.put(API_URL.budget_loa_groups + id + '/', data);
  }

  removeLOA(id: number) {
    return this.http.delete(API_URL.loa_groups + id + '/');
  }

  removeBudgetLOA(id: number) {
    return this.http.delete(API_URL.budget_loa_groups + id + '/');
  }

  getLOAData(id: any, params?: LoaParams) {
    return this.http.get(API_URL.loa_groups + id + '/', params);
  }

  getBudgetLOAData(id: any, params?: LoaParams) {
    return this.http.get(
      API_URL.budget_loa_groups + id + '/',
      params,
    );
  }

  initDynamicLOAMembers(loaId: any, departmentId: any) {
    return this.http.post(
      API_URL.loa_groups +
        loaId +
        '/init-dynamic-members/?include_custom=True',
      {
        department_id: departmentId,
      },
    );
  }

  initDynamicLOASignaturePosition(
    templateId: number,
    loaId: number,
    loaData: LoaDetail,
    signaturePosition: Marker[] | null = null,
  ) {
    return this.http.post(API_URL.init_sig_pos, {
      id: templateId,
      loa_id: loaId,
      loa_data: loaData,
      signature_position: signaturePosition,
    });
  }

  batchUploadFile(data: any, type: any) {
    return this.http.post(`/api/batch-uploads/${type}/`, data);
  }

  validateApprover(signer: Partial<Member>): boolean | any {
    let isValid = false;
    if (signer.person == null && signer.no_person) {
      isValid = true;
    } else if (signer.person == null) {
      isValid = false;
    } else if (typeof signer.person === 'object') {
      isValid = [
        'email',
        'name',
        'job_position',
        'phone_number',
      ].every((validateKey) => {
        return !!(signer.person as NonUser | any)?.[validateKey];
      });
    } else if (typeof signer.person === 'number') {
      isValid = true;
    } else {
      isValid = false;
    }
    return isValid;
  }

  validateCustomLOA(
    loaData: any,
    allowNoPerson = false,
    excludeLvlFn?: (level: any) => boolean,
  ): string[] {
    const msg = [];
    const chk = checkEmptyApprover(
      loaData?.levels,
      allowNoPerson,
      excludeLvlFn,
    );
    if (!chk) {
      msg.push(
        this.translate.instant(
          'FORM.Please fill in approver details',
        ),
      );
    }
    if (!checkMail(loaData?.levels)) {
      msg.push(this.translate.instant('MEMOS.LOA-EMAIL-INVALID'));
    }
    if (!checkPhoneNumber(loaData?.levels)) {
      msg.push(
        this.translate.instant(
          'MEMOS.Please enter the approver phone number with 10 digits',
        ),
      );
    }
    return msg;
  }

  validateDynamicLOA(loaData: any): string[] {
    const msg = new Set<string>();
    loaData?.levels?.forEach((level: any) => {
      switch (level.user_type) {
        case LoaUserTypeEnum.JobPosition: {
          const hasEmpty = level?.members?.some(
            (m: any) => !m?.job_position,
          );
          if (hasEmpty) {
            msg.add(
              this.translate.instant(
                'DYNAMIC-LOA.Job position is required',
              ),
            );
          }
          break;
        }
        case LoaUserTypeEnum.Loh: {
          const hasEmpty = level?.members?.some(
            (m: any) => !m?.level_of_hierarchy,
          );
          if (hasEmpty) {
            msg.add(
              this.translate.instant(
                'DYNAMIC-LOA.Level of hierarchy is required',
              ),
            );
          }
          break;
        }
      }
    });
    return Array.from(msg);
  }
}
